import useAuth from 'hooks/useAuth';
import useMobile from 'hooks/useMobile';
import debounce from 'lodash.debounce';
import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import SimpleBar from 'simplebar-react';

import SidebarLinkGroup from './SidebarLinkGroup';

import urbanekaLogo from 'assets/images/urbaneka-logo.svg';

type booleanSetterCallback = (prev: boolean)=>boolean

type Prop = {
  sidebarOpen : boolean;
  setSidebarOpen : (a:boolean | booleanSetterCallback)=>void
}

function Sidebar({ sidebarOpen, setSidebarOpen }:Prop) {
  const location = useLocation();
  const { pathname } = location;

  const trigger = useRef<HTMLButtonElement>(null);
  const sidebar = useRef<HTMLDivElement>(null);
  const simplebar = useRef<SimpleBar>(null);

  const [screenHeight, setScreenHeight] = useState(window.innerHeight);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const isMobile = useMobile();

  const {auth} = useAuth()


  //recalculate simplebar height on screen resize
  useEffect(()=>{
    const recalculateSimplebar = debounce(()=>{
      setScreenHeight(window.innerHeight)
    },20)
    window.addEventListener('resize', recalculateSimplebar)
    return ()=>{
      window.removeEventListener('resize', recalculateSimplebar)
    }
  }, [])

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }:MouseEvent) => {
      if (!sidebar.current || !trigger.current) return;
      if (!sidebarOpen || sidebar.current.contains(target as Node) || trigger.current.contains(target as Node)) return;
      setSidebarOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ key }:KeyboardEvent) => {
      if (!sidebarOpen || key !== 'Escape') return;
      setSidebarOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  useEffect(() => {
    localStorage.setItem('sidebar-expanded', JSON.stringify(sidebarExpanded));
    if (sidebarExpanded) {
      document.querySelector('body')!.classList.add('sidebar-expanded');
    } else {
      document.querySelector('body')!.classList.remove('sidebar-expanded');
    }
  }, [sidebarExpanded]);

  return (
    <div>
      {/* Sidebar backdrop (mobile only) */}
      <div
        className={`fixed inset-0 bg-slate-900 bg-opacity-30 z-10 lg:hidden lg:z-auto transition-opacity duration-200 ${
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden="true"
      ></div>

      {/* Sidebar */}
      <div
        onMouseEnter={()=>setSidebarExpanded(true)}
        onMouseLeave={()=>setSidebarExpanded(false)}
        id="sidebar"
        ref={sidebar}
        className={`flex flex-col absolute z-10 left-0 top-0 lg:sticky lg:translate-x-0 h-screen w-64 lg:w-20 lg:sidebar-expanded:!w-64 shrink-0 bg-slate-800 transition-all duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-64'
        }`}
      >
        <SimpleBar className='p-4' style={{maxHeight: screenHeight}} ref={simplebar} id="sidebar-simplebar">
          <div className="flex justify-between mb-10 pr-3 sm:px-2">
            {/* Close button */}
            <button
              ref={trigger}
              className="lg:hidden text-slate-500 hover:text-slate-400"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-controls="sidebar"
              aria-expanded={sidebarOpen}
            >
              <span className="sr-only">Close sidebar</span>
              <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
              </svg>
            </button>
            {/* Logo */}
            <NavLink end to="/" className='text-white font-bold block'>
              { isMobile || sidebarExpanded ?
                  <div>
                    URBANEKA
                  </div> :
                  <div>
                    <img src={urbanekaLogo} alt="" />
                  </div>
              }
            </NavLink>
          </div>
          {/* Links */}
          <div className="space-y-8">
            {/* Pages group */}
            <div>
              <h3 className="text-xs uppercase text-slate-500 font-semibold pl-3">
                <span className="hidden lg:block lg:sidebar-expanded:hidden text-center w-6" aria-hidden="true">
                  •••
                </span>
                <span className="lg:hidden lg:sidebar-expanded:block ">Pages</span>
              </h3>
              <ul className="mt-3">
                {/* Dashboard */}
                <SidebarLinkGroup activecondition={pathname === '/admin'}>
                  {(handleClick, open) => {
                    return (
                      <>
                        <a
                          href="#0"
                          className={`block text-slate-200 hover:text-white truncate transition duration-150 ${
                            (pathname === '/admin') && 'hover:text-slate-200'
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            sidebarExpanded ? handleClick() : setSidebarExpanded(true);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <svg className="shrink-0 h-6 w-6" viewBox="0 0 24 24">
                                <path
                                  className={`fill-current text-slate-400 ${
                                    (pathname === '/admin') && '!text-indigo-500'
                                  }`}
                                  d="M12 0C5.383 0 0 5.383 0 12s5.383 12 12 12 12-5.383 12-12S18.617 0 12 0z"
                                />
                                <path
                                  className={`fill-current text-slate-600 ${(pathname === '/admin') && 'text-indigo-600'}`}
                                  d="M12 3c-4.963 0-9 4.037-9 9s4.037 9 9 9 9-4.037 9-9-4.037-9-9-9z"
                                />
                                <path
                                  className={`fill-current text-slate-400 ${(pathname === '/admin') && 'text-indigo-200'}`}
                                  d="M12 15c-1.654 0-3-1.346-3-3 0-.462.113-.894.3-1.285L6 6l4.714 3.301A2.973 2.973 0 0112 9c1.654 0 3 1.346 3 3s-1.346 3-3 3z"
                                />
                              </svg>
                              <span className="text-sm font-medium ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                Dashboard
                              </span>
                            </div>
                            {/* Icon */}
                            <div className="flex shrink-0 ml-2">
                              <svg className={`w-3 h-3 shrink-0 ml-1 fill-current text-slate-400 ${open && 'rotate-180'}`} viewBox="0 0 12 12">
                                <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                              </svg>
                            </div>
                          </div>
                        </a>
                        <div className="lg:hidden lg:sidebar-expanded:block ">
                          <ul className={`pl-9 mt-1 ${!open && 'hidden'}`}>
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                end
                                to="/admin"
                                className={({ isActive }) =>
                                  'block text-slate-400 hover:text-slate-200 transition duration-150 truncate ' + (isActive ? '!text-indigo-500' : '')
                                }
                              >
                                <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  Main
                                </span>
                              </NavLink>
                            </li>
                          </ul>
                        </div>
                      </>
                    );
                  }}
                </SidebarLinkGroup>
                {/* E-Commerce */}
                <SidebarLinkGroup activecondition={pathname.includes('data')}>
                  {(handleClick, open) => {
                    return (
                      <React.Fragment>
                        <a
                          href="#0"
                          className={`block text-slate-200 hover:text-white truncate transition duration-150 ${
                            pathname.includes('data') && 'hover:text-slate-200'
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            sidebarExpanded ? handleClick() : setSidebarExpanded(true);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <svg className="shrink-0 h-6 w-6" viewBox="0 0 24 24">
                                <path
                                  className={`fill-current text-slate-400 ${pathname.includes('data') && 'text-indigo-300'}`}
                                  d="M13 15l11-7L11.504.136a1 1 0 00-1.019.007L0 7l13 8z"
                                />
                                <path
                                  className={`fill-current text-slate-700 ${pathname.includes('data') && '!text-indigo-600'}`}
                                  d="M13 15L0 7v9c0 .355.189.685.496.864L13 24v-9z"
                                />
                                <path
                                  className={`fill-current text-slate-600 ${pathname.includes('data') && 'text-indigo-500'}`}
                                  d="M13 15.047V24l10.573-7.181A.999.999 0 0024 16V8l-11 7.047z"
                                />
                              </svg>
                              <span className="text-sm font-medium ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                Manage data
                              </span>
                            </div>
                            {/* Icon */}
                            <div className="flex shrink-0 ml-2">
                              <svg className={`w-3 h-3 shrink-0 ml-1 fill-current text-slate-400 ${open && 'rotate-180'}`} viewBox="0 0 12 12">
                                <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                              </svg>
                            </div>
                          </div>
                        </a>
                        <div className="lg:hidden lg:sidebar-expanded:block ">
                          <ul className={`pl-9 mt-1 ${!open && 'hidden'}`}>
                            { auth?.isAdmin &&
                              <li className="mb-1 last:mb-0">
                                <NavLink
                                  end
                                  to="/admin/data/user"
                                  className={({isActive}) => "block text-slate-400 hover:text-slate-200 transition duration-150 truncate " + (isActive ? '!text-indigo-500' : '')}
                                >
                                  <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                    Users
                                  </span>
                                </NavLink>
                              </li>
                            }
                            {
                              auth?.isAdmin && <li className="mb-1 last:mb-0">
                                <NavLink
                                  end
                                  to="/admin/data/payments"
                                  className={({isActive}) => "block text-slate-400 hover:text-slate-200 transition duration-150 truncate " + (isActive ? '!text-indigo-500' : '')}
                                >
                                  <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                    Payments
                                  </span>
                                </NavLink>
                              </li>
                            }
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                end
                                to="/admin/data/businesses"
                                className={({isActive}) => "block text-slate-400 hover:text-slate-200 transition duration-150 truncate " + (isActive ? '!text-indigo-500' : '')}
                              >
                                <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  Businesses
                                </span>
                              </NavLink>
                            </li>
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                end
                                to="/admin/data/blogs"
                                className={({isActive}) => "block text-slate-400 hover:text-slate-200 transition duration-150 truncate " + (isActive ? '!text-indigo-500' : '')}
                              >
                                <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  Blogs
                                </span>
                              </NavLink>
                            </li>
                            {
                              auth?.isAdmin && <li className="mb-1 last:mb-0">
                                <NavLink
                                  end
                                  to="/admin/data/events"
                                  className={({isActive}) => "block text-slate-400 hover:text-slate-200 transition duration-150 truncate " + (isActive ? '!text-indigo-500' : '')}
                                >
                                  <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                    Events
                                  </span>
                                </NavLink>
                              </li>
                            }
                            {
                              auth?.isAdmin && <li className="mb-1 last:mb-0">
                                <NavLink
                                  end
                                  to="/admin/data/job-posts"
                                  className={({isActive}) => "block text-slate-400 hover:text-slate-200 transition duration-150 truncate " + (isActive ? '!text-indigo-500' : '')}
                                >
                                  <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                    Job Posts
                                  </span>
                                </NavLink>
                              </li>
                            }
                            
                          </ul>
                        </div>
                      </React.Fragment>
                    );
                  }}
                </SidebarLinkGroup>
                { auth?.isAdmin &&
                  <SidebarLinkGroup activecondition={pathname.includes('setting')}>
                    {(handleClick, open) => {
                      return (
                        <React.Fragment>
                          <a
                            href="#0"
                            className={`block text-slate-200 hover:text-white truncate transition duration-150 ${
                              pathname.includes('setting') && 'hover:text-slate-200'
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              sidebarExpanded ? handleClick() : setSidebarExpanded(true);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <svg className="shrink-0 h-6 w-6" viewBox="0 0 24 24">
                                  <path
                                    className={`fill-current text-slate-400 ${pathname.includes('setting') && 'text-indigo-300'}`}
                                    d="M13 15l11-7L11.504.136a1 1 0 00-1.019.007L0 7l13 8z"
                                  />
                                  <path
                                    className={`fill-current text-slate-700 ${pathname.includes('setting') && '!text-indigo-600'}`}
                                    d="M13 15L0 7v9c0 .355.189.685.496.864L13 24v-9z"
                                  />
                                  <path
                                    className={`fill-current text-slate-600 ${pathname.includes('setting') && 'text-indigo-500'}`}
                                    d="M13 15.047V24l10.573-7.181A.999.999 0 0024 16V8l-11 7.047z"
                                  />
                                </svg>
                                <span className="text-sm font-medium ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  Setting
                                </span>
                              </div>
                              {/* Icon */}
                              <div className="flex shrink-0 ml-2">
                                <svg className={`w-3 h-3 shrink-0 ml-1 fill-current text-slate-400 ${open && 'rotate-180'}`} viewBox="0 0 12 12">
                                  <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                                </svg>
                              </div>
                            </div>
                          </a>
                          <div className="lg:hidden lg:sidebar-expanded:block ">
                            <ul className={`pl-9 mt-1 ${!open && 'hidden'}`}>
                              <li className="mb-1 last:mb-0">
                                <NavLink
                                  end
                                  to="/admin/setting/site"
                                  className={({isActive}) => "block text-slate-400 hover:text-slate-200 transition duration-150 truncate " + (isActive ? '!text-indigo-500' : '')}
                                >
                                  <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                    Site Setting
                                  </span>
                                </NavLink>
                              </li>
                              <li className="mb-1 last:mb-0">
                                <NavLink
                                  end
                                  to="/admin/setting/admin"
                                  className={({isActive}) => "block text-slate-400 hover:text-slate-200 transition duration-150 truncate " + (isActive ? '!text-indigo-500' : '')}
                                >
                                  <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                    App Setting
                                  </span>
                                </NavLink>
                              </li>

                            </ul>
                          </div>
                        </React.Fragment>
                      );
                    }}
                  </SidebarLinkGroup>
                }
              </ul>
            </div>
          </div>
        </SimpleBar>


      </div>
    </div>
  );
}

export default Sidebar;